/**
 * RTSummaryPanel.jsx
 *
 * Painel de "Resumo da RT para o ClickUp".
 *
 * Aparece no cabeçalho do LeadDetail assim que a RT é confirmada
 * (`status === 'qt_realizada'`) e enquanto o Consultor ainda não
 * publicou o resumo para o canal SAC do ClickUp (não tem
 * `rt_summary_sent_at`). O objectivo é dar visibilidade imediata à
 * equipa SAC sobre o desfecho da RT — fechado, perdido ou em pausa.
 *
 * Fluxo:
 *   1. Consultor escolhe o desfecho (fechado / perdido / em análise).
 *   2. Se perdido → escolhe razão + comentário livre.
 *   3. Clica "Gerar rascunho com IA" → chamada
 *      POST /api/leads/{id}/rt-summary/generate.
 *   4. Edita à vontade no textarea (autosave a cada 5s via PATCH).
 *   5. Clica "Enviar para ClickUp" → chamada
 *      POST /api/leads/{id}/rt-summary/send. Painel bloqueia.
 *
 * Se `lead.rt_summary_sent_at` já existir, o painel mostra apenas o
 * badge "Enviado" com quem/quando.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Send, Loader2, MessageSquareText, CheckCircle2,
  AlertTriangle, Ban, Clock, Pencil, X, RefreshCw,
} from "lucide-react";
import api from "@/lib/api";

const DEAL_OPTIONS = [
  { value: "closed",  label: "Negócio fechado",   icon: CheckCircle2, cls: "text-[hsl(var(--success))] border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))]" },
  { value: "lost",    label: "Negócio perdido",   icon: Ban,           cls: "text-[hsl(var(--danger))] border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger-bg))]" },
  { value: "pending", label: "Cliente a decidir", icon: Clock,         cls: "text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))]" },
];

const LOST_REASONS = [
  "Demasiado caro",
  "Escolheu outro prestador",
  "Adiado indefinidamente",
  "Familiares em desacordo",
  "Sem urgência real",
  "Outro",
];

export default function RTSummaryPanel({ lead, onSaved }) {
  const alreadySent = !!lead?.rt_summary_sent_at;
  // 05/02/2026 — Modo reedição: quando ligado enquanto `alreadySent`,
  // mostramos o mesmo formulário editável mas o submit vai para o
  // endpoint /republish (que faz UPDATE da task ClickUp existente).
  const [isEditing, setIsEditing] = useState(false);
  const isRepublishing = alreadySent && isEditing;

  const [dealStatus, setDealStatus] = useState(lead?.rt_summary_deal_status || "pending");
  const [dealReason, setDealReason] = useState(lead?.rt_summary_deal_reason || "");
  const [consultantNotes, setConsultantNotes] = useState("");
  const [draft, setDraft] = useState(lead?.rt_summary_draft || "");
  const [busy, setBusy] = useState("");
  const saveTimer = useRef(null);
  const skipAutosave = useRef(true);
  const chosen = useMemo(() => DEAL_OPTIONS.find((o) => o.value === dealStatus), [dealStatus]);

  // Sync when lead reloads
  useEffect(() => {
    setDealStatus(lead?.rt_summary_deal_status || "pending");
    setDealReason(lead?.rt_summary_deal_reason || "");
    setDraft(lead?.rt_summary_draft || "");
    skipAutosave.current = true;
  }, [lead?.id, lead?.rt_summary_sent_at]);

  // Autosave draft edits (debounced 5s) — só depois de o utilizador editar
  useEffect(() => {
    if (skipAutosave.current) { skipAutosave.current = false; return; }
    if (alreadySent || !draft.trim()) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/leads/${lead.id}/rt-summary`, {
          draft, deal_status: dealStatus, deal_reason: dealReason || null,
        });
      } catch (e) { /* silencioso — próximo save tenta de novo */ }
    }, 5000);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, dealStatus, dealReason]);

  const generate = async () => {
    if (dealStatus === "lost" && !dealReason.trim()) {
      toast.error("Escolha a razão de perda antes de gerar o resumo.");
      return;
    }
    setBusy("gen");
    try {
      const { data } = await api.post(`/leads/${lead.id}/rt-summary/generate`, {
        deal_status: dealStatus,
        deal_reason: dealReason || null,
        consultant_notes: consultantNotes || null,
      });
      setDraft(data.draft);
      skipAutosave.current = true;   // não faz PATCH imediato
      toast.success("Rascunho gerado — reveja e edite antes de enviar.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a gerar o rascunho");
    } finally { setBusy(""); }
  };

  const sendToClickup = async () => {
    if (draft.trim().length < 40) {
      toast.error("O resumo está demasiado curto — reveja antes de enviar.");
      return;
    }
    const isRepub = alreadySent && isEditing;
    const confirmMsg = isRepub
      ? "Confirmar republicação — actualiza a task no ClickUp com o novo texto?"
      : "Confirmar envio do resumo para o canal SAC do ClickUp?";
    if (!window.confirm(confirmMsg)) return;
    setBusy(isRepub ? "repub" : "send");
    try {
      const endpoint = isRepub ? "republish" : "send";
      await api.post(`/leads/${lead.id}/rt-summary/${endpoint}`, {
        draft, deal_status: dealStatus, deal_reason: dealReason || null,
      });
      toast.success(isRepub ? "Resumo actualizado no ClickUp!" : "Resumo enviado para o ClickUp!");
      setIsEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar para o ClickUp");
    } finally { setBusy(""); }
  };

  // Estado enviado — mostra badge + texto do resumo + link para o ClickUp.
  // Se ``isEditing`` estiver ligado, saltamos para o formulário editável
  // e o submit chama ``/republish`` em vez de ``/send``.
  if (alreadySent && !isEditing) {
    const sentBody = lead.rt_summary_draft || "(resumo não disponível)";
    const clickupUrl = lead.clickup_rt_task_url;
    const sentDealStatus = lead.rt_summary_deal_status;
    const dealOpt = DEAL_OPTIONS.find((d) => d.value === sentDealStatus);
    const updatedAt = lead.rt_summary_updated_at;
    const publishCount = Number(lead.rt_summary_publish_count || 1);
    return (
      <div data-testid="rt-summary-sent"
           className="rounded-xl border border-[hsl(var(--success))]/30 bg-[hsl(var(--success-bg))]/50 overflow-hidden">
        {/* Cabeçalho */}
        <div className="flex items-center gap-3 px-4 py-3 bg-[hsl(var(--success-bg))]/70 border-b border-[hsl(var(--success))]/20">
          <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[hsl(var(--success))]">
              Resumo da RT enviado para o ClickUp
            </div>
            <div className="text-[11px] text-[hsl(var(--success))]/85">
              {lead.rt_summary_sent_by_name || "—"} · {new Date(lead.rt_summary_sent_at).toLocaleString("pt-PT")}
              {updatedAt && (
                <span> · <span className="italic">actualizado {new Date(updatedAt).toLocaleString("pt-PT")}
                  {lead.rt_summary_updated_by_name ? ` por ${lead.rt_summary_updated_by_name}` : ""}
                </span></span>
              )}
            </div>
          </div>
          {publishCount > 1 && (
            <span
              title={`Publicado ${publishCount} vezes`}
              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[hsl(var(--success))] bg-card border border-[hsl(var(--success))]/25 rounded-full px-2 py-1">
              v{publishCount}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[hsl(var(--success))] bg-card border border-[hsl(var(--success))]/25 rounded-full px-2 py-1">
            Publicado
          </span>
        </div>
        {/* Corpo — desfecho + texto do resumo */}
        <div className="p-4 space-y-3 bg-card/60">
          {dealOpt && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Desfecho:
              </span>
              <span className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1 border ${dealOpt.cls}`}>
                <dealOpt.icon className="w-3 h-3" />
                {dealOpt.label}
              </span>
              {lead.rt_summary_deal_reason && (
                <span className="text-[11px] text-muted-foreground italic ml-1">
                  — {lead.rt_summary_deal_reason}
                </span>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Resumo publicado
            </div>
            <div data-testid="rt-summary-sent-body"
                 className="rounded-lg border border-border bg-background/60 px-3 py-2.5 text-sm whitespace-pre-wrap leading-relaxed max-h-[280px] overflow-auto">
              {sentBody}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {clickupUrl && (
              <a href={clickupUrl} target="_blank" rel="noopener noreferrer"
                 data-testid="rt-summary-clickup-link"
                 className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
                <MessageSquareText className="w-3.5 h-3.5" />
                Abrir tarefa no ClickUp
              </a>
            )}
            <button
              type="button"
              data-testid="rt-summary-edit"
              onClick={() => setIsEditing(true)}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border border-border bg-card hover:bg-muted"
              title="Reeditar e republicar — actualiza a task no ClickUp com o novo texto">
              <Pencil className="w-3.5 h-3.5" />
              Reeditar e republicar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="rt-summary-panel"
         className={`rounded-xl border-2 shadow-sm overflow-hidden ${
           isRepublishing
             ? "border-primary bg-primary/5"
             : "border-orange-400 bg-[hsl(var(--warning-bg))]/60"
         }`}>
      {/* Header — banner de aviso (varia entre "envio inicial" e "reedição") */}
      <div className={`flex items-center gap-2 px-4 py-2.5 text-primary-foreground text-sm font-semibold ${
        isRepublishing ? "bg-primary" : "bg-orange-500"
      }`}>
        {isRepublishing ? <RefreshCw className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        <span>
          {isRepublishing
            ? "Reeditar resumo — a task no ClickUp será actualizada"
            : "Resumo da RT — pendente de envio para o ClickUp"}
        </span>
        {isRepublishing && (
          <button
            type="button"
            data-testid="rt-summary-cancel-edit"
            onClick={() => {
              setDraft(lead?.rt_summary_draft || "");
              setDealStatus(lead?.rt_summary_deal_status || "pending");
              setDealReason(lead?.rt_summary_deal_reason || "");
              setIsEditing(false);
            }}
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest bg-white/15 hover:bg-white/25 rounded-full px-3 py-1"
            title="Cancelar edição — mantém o resumo anterior"
          >
            <X className="w-3 h-3" /> Cancelar
          </button>
        )}
        {!isRepublishing && (
          <span className="ml-auto text-[10px] uppercase tracking-widest opacity-80">
            Consultor
          </span>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Passo 1 — desfecho */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            1. Desfecho da RT
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {DEAL_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const active = dealStatus === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`rt-deal-${opt.value}`}
                  onClick={() => setDealStatus(opt.value)}
                  className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-semibold transition-all ${active ? opt.cls + " ring-2 ring-offset-1 ring-slate-400" : "bg-card border-border text-muted-foreground hover:bg-muted/40"}`}
                >
                  <Icon className="w-4 h-4" />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Razão (só se perdido) */}
        {dealStatus === "lost" && (
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              2. Razão da perda
            </div>
            <select
              data-testid="rt-lost-reason"
              value={dealReason}
              onChange={(e) => setDealReason(e.target.value)}
              className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-red-500"
            >
              <option value="">— Escolha uma razão —</option>
              {LOST_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}

        {/* Notas extra */}
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Notas do Consultor (opcional — ajudam a IA)
          </div>
          <textarea
            data-testid="rt-consultant-notes"
            rows={2}
            value={consultantNotes}
            onChange={(e) => setConsultantNotes(e.target.value)}
            placeholder="Ex: cliente já tinha um cuidador anterior a €12/h e não aceitou o nosso preço; pediu para voltarmos a ligar dia 15..."
            className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:border-orange-500"
          />
        </div>

        {/* Botão gerar */}
        <div className="flex items-center gap-2">
          <button
            data-testid="rt-summary-generate"
            onClick={generate}
            disabled={busy !== "" || (dealStatus === "lost" && !dealReason.trim())}
            className="inline-flex items-center gap-2 rounded-full bg-foreground hover:bg-foreground/90 text-background text-xs font-semibold px-4 py-2 disabled:opacity-40"
          >
            {busy === "gen" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {draft ? "Regenerar rascunho" : "Gerar rascunho com IA"}
          </button>
          {chosen && (
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${chosen.cls}`}>
              {chosen.label}
            </span>
          )}
        </div>

        {/* Rascunho editável */}
        {draft && (
          <div className="space-y-2 pt-2 border-t border-[hsl(var(--warning))]/25">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Rascunho (edite antes de enviar)
              </div>
              <div className="text-[10px] text-muted-foreground">
                <MessageSquareText className="w-3 h-3 inline mr-1" />
                {draft.length} caracteres · auto-save em 5s
              </div>
            </div>
            <textarea
              data-testid="rt-summary-draft"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={12}
              className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm font-mono text-foreground resize-y focus:outline-none focus:border-orange-500 leading-relaxed"
            />
            <div className="flex justify-end">
              <button
                data-testid="rt-summary-send"
                onClick={sendToClickup}
                disabled={busy !== "" || draft.trim().length < 40}
                className={`inline-flex items-center gap-2 rounded-full text-primary-foreground text-sm font-semibold px-5 py-2.5 disabled:opacity-40 ${
                  isRepublishing
                    ? "bg-primary hover:bg-primary/90"
                    : "bg-orange-600 hover:bg-orange-700"
                }`}
              >
                {(busy === "send" || busy === "repub") ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isRepublishing ? (
                  <RefreshCw className="w-4 h-4" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {isRepublishing ? "Actualizar no ClickUp" : "Enviar para o ClickUp SAC"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
